"use client"

import { useState, useEffect, useRef } from "react"
import { useTheme } from "next-themes"
import { motion, AnimatePresence } from "framer-motion"
import { Archive, ChevronLeft, ChevronRight, Search, Filter, X, ArrowDownAZ, SortDescending, AlertTriangle, Clock, EyeOff, Eye, RotateCcw } from "@/lib/icons"
import { LeadCard } from "./lead-card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { updateLead, autoArchiveStaleLeads } from "@/lib/supabase"
import KanbanColumnMobile from "./kanban-mobile" // <-- nuevo componente para móvil
import { getDaysSinceUpdate, getLastInteractionDateOrEpoch } from "@/lib/dates"

const AUTO_ARCHIVE_INTERVAL_MS = 1000 * 60 * 60 * 24 // sólo una vez cada 24h por navegador
const AUTO_ARCHIVE_STORAGE_KEY = "kanban:autoArchive:lastRun"
const AUTO_ARCHIVE_RUNNING_FLAG = "__kanbanAutoArchiveRunning"

export function KanbanColumn({
  column,
  leads,
  onViewDetails,
  onOpenConversation,
  onDragStart,
  onDragEnd,
  onDrop,
  onArchive,
  isDragging,
  statsCount,
  totalLeads,
  highlightedLeadId,
}) {
  // Pagination is handled via page/itemsPerPage/filteredLeads below

  // BASE: columnas más altas para que quepan más prospectos visibles sin paginar tan seguido
  const COLUMN_BASE_MIN_HEIGHT = "calc(100vh - 100px)"
  const columnWidthClamp = "clamp(420px, 16vw, 270px)"
  const minColumnHeightStyle = COLUMN_BASE_MIN_HEIGHT

  // estado para indicar arrastre sobre la columna (muestra borde punteado)
  const [dragOver, setDragOver] = useState(false)
  const [archiveZoneHover, setArchiveZoneHover] = useState(false)

  // evitar archivar varias veces la misma tarjeta desde este efecto
  const processedArchiveRef = useRef(new Set())

  // fondo base y fondo cuando se está arrastrando (respetando dark mode)
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"
  const accent = column?.accent || "#6366f1"
  const headerBackground = isDark
    ? `linear-gradient(135deg, color-mix(in srgb, ${accent} 25%, #180509), color-mix(in srgb, ${accent} 8%, #1f080c))` // Tonos más acordes al tinto (dark)
    : `linear-gradient(135deg, color-mix(in srgb, ${accent} 15%, #fdf2f4), color-mix(in srgb, ${accent} 5%, #ffffff))` // Tonos más sutiles en light mode

  const baseBackground = isDark
    ? "linear-gradient(135deg, rgba(20,6,9,0.85) 0%, rgba(26,7,11,0.95) 100%)" // Background tinto súper oscuro
    : "linear-gradient(135deg, #ffffff 90%, #fdf8f9 100%)" // Light mode sutil con touch cálido

  const dragOverBackground = isDark ? "#2e0f15" : "#fdf2f4"

  const ColumnIcon = column.icon

  const [page, setPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25) // 25 leads por página (más visibles por scroll vertical)
  // Vista compacta deshabilitada por preferencia del usuario — siempre normal.
  const isCompactView = false
  const [searchTerm, setSearchTerm] = useState("")
  const [showFilters, setShowFilters] = useState(false)
  const [sortOrder, setSortOrder] = useState("recent")
  const [filteredLeads, setFilteredLeads] = useState([])

  // Leads ocultos (por columna, persistido en localStorage)
  const HIDDEN_STORAGE_KEY = `kanban_hidden_leads_${column.id}`
  const [hiddenLeadIds, setHiddenLeadIds] = useState(() => {
    try {
      const stored = localStorage.getItem(HIDDEN_STORAGE_KEY)
      return stored ? new Set(JSON.parse(stored)) : new Set()
    } catch { return new Set() }
  })

  const persistHidden = (nextSet) => {
    setHiddenLeadIds(nextSet)
    try { localStorage.setItem(HIDDEN_STORAGE_KEY, JSON.stringify([...nextSet])) } catch {}
  }

  const handleHideLead = (leadId) => {
    const next = new Set(hiddenLeadIds)
    next.add(leadId)
    persistHidden(next)
  }

  const restoreHiddenLeads = () => {
    persistHidden(new Set())
  }

  const hiddenCountInColumn = leads.filter(l => hiddenLeadIds.has(l.id)).length

  const highlightedRef = useRef(null)
  const containerRef = useRef(null)

  // --- NUEVO: detectar si el lead rastreado está en esta columna para borde punteado ---
  const hasTrackedLead = Boolean(highlightedLeadId && leads.some(l => l.id === highlightedLeadId))

  useEffect(() => {
    let result = [...leads]

    // Filtrar leads ocultos
    if (hiddenLeadIds.size > 0) {
      result = result.filter(lead => !hiddenLeadIds.has(lead.id))
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      result = result.filter(lead =>
        (lead.nombre_tutor && lead.nombre_tutor.toLowerCase().includes(term)) ||
        (lead.telefono && lead.telefono.includes(term)) ||
        (lead.email && lead.email.toLowerCase().includes(term)) ||
        (lead.tags && String(lead.tags).toLowerCase().includes(term))
      )
    }

    // Orden de prioridad por calidad del lead (caliente primero, spam último)
    const calidadOrder = { caliente: 1, tibio: 2, frio: 3, spam: 4 }

    const getCalidadPriority = (lead) => {
      const calidad = String(lead.calidad_lead || "tibio").toLowerCase()
      return calidadOrder[calidad] || 2 // default tibio = 2
    }

    switch (sortOrder) {
      case "name":
        result.sort((a, b) => (a.nombre_tutor || "").localeCompare(b.nombre_tutor || ""))
        break
      case "newest":
        result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        break
      case "oldest":
        result.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))
        break
      case "last_activity":
        result.sort((a, b) => {
          const dateA = getLastInteractionDateOrEpoch(b)
          const dateB = getLastInteractionDateOrEpoch(a)
          return dateA - dateB
        })
        break
      case "priority":
        const priorityOrder = { alta: 1, media: 2, baja: 3 }
        result.sort((a, b) => {
          const prioA = priorityOrder[a.prioridad] || 999
          const prioB = priorityOrder[b.prioridad] || 999
          if (prioA !== prioB) return prioA - prioB

          const daysA = getDaysSinceUpdate(a)
          const daysB = getDaysSinceUpdate(b)
          return daysB - daysA
        })
        break
      case "recent":
      default:
        // Orden por recencia de CREACIÓN: el lead recién llegado arriba.
        // Tiebreaker con updated_at para casos sin created_at.
        result.sort((a, b) => {
          const tA = new Date(a.created_at || a.updated_at || 0).getTime()
          const tB = new Date(b.created_at || b.updated_at || 0).getTime()
          return tB - tA  // descendente: el más reciente creado primero
        })
    }

    setFilteredLeads(result)
  }, [leads, searchTerm, sortOrder, hiddenLeadIds])

  // Volver a la primera página SOLO cuando el usuario cambia la vista
  // (búsqueda u orden). NO debe dispararse al refrescarse `leads` —realtime,
  // borrado, abrir un prospecto—, que era justo lo que reseteaba la paginación.
  useEffect(() => {
    setPage(1)
  }, [searchTerm, sortOrder])

  useEffect(() => {
    if (highlightedLeadId && highlightedRef.current) {
      highlightedRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  }, [highlightedLeadId])

  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / itemsPerPage))
  const paginatedLeads = filteredLeads.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  )
  const startIndex = (page - 1) * itemsPerPage
  const endIndex = Math.min(page * itemsPerPage, filteredLeads.length)

  // Si la página actual quedó fuera de rango tras un borrado/refresco,
  // ajusta a la última página válida en lugar de saltar a la primera.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  const nextPage = () => {
    if (page < totalPages) {
      setPage(page + 1)
      if (containerRef.current) {
        containerRef.current.scrollTop = 0
      }
    }
  }

  const prevPage = () => {
    if (page > 1) {
      setPage(page - 1)
      if (containerRef.current) {
        containerRef.current.scrollTop = 0
      }
    }
  }

  // --- Manejo drag para borde punteado por columna (usa dragOver local) ---
  const handleDragOver = (event) => {
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = "move"
    setDragOver(true)
  }

  const handleDragLeave = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setDragOver(false)
  }

  const handleDrop = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setDragOver(false)
    onDrop(column.id)
  }

  const clearFilters = () => {
    setSearchTerm("")
    setSortOrder("recent")
    setShowFilters(false)
    restoreHiddenLeads()
    setPage(1)
  }

  const gridClassName = 'grid-cols-1'
  const itemsPerPageOptions = [
    { value: 5, label: '5 por pág.' },
    { value: 10, label: '10 por pág.' },
    { value: 15, label: '15 por pág.' },
    { value: 20, label: '20 por pág.' },
  ]
  // Tarjetas aún más compactas: ajustar min-heights para vista compacta y normal
  const leadMinHeight = isCompactView ? '40px' : '76px'

  // Colores de urgencia alineados con sistema de recordatorios (3, 5, 14 días)
  const getUrgencyClass = (lead) => {
    const days = getDaysSinceUpdate(lead)
    if (days >= 5) return "border border-rose-100 bg-rose-50/70 dark:border-rose-900/20 dark:bg-rose-950/6"
    if (days >= 3) return "border border-amber-100 bg-amber-50/70 dark:border-amber-900/20 dark:bg-amber-950/6"
    return "border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
  }

  // Auto-archivar en lote: validar máximo una vez cada 24h por navegador.
  useEffect(() => {
    if (typeof window === "undefined") return
    let mounted = true

    const shouldRun = () => {
      try {
        const lastRun = Number(localStorage.getItem(AUTO_ARCHIVE_STORAGE_KEY) || 0)
        if (!lastRun) return true
        return Date.now() - lastRun >= AUTO_ARCHIVE_INTERVAL_MS
      } catch {
        return true
      }
    }

    const markRun = () => {
      try {
        localStorage.setItem(AUTO_ARCHIVE_STORAGE_KEY, String(Date.now()))
      } catch {
        // ignore storage errors
      }
    }

    const runAutoArchive = async () => {
      if (!mounted) return
      if (!shouldRun()) return
      if (window[AUTO_ARCHIVE_RUNNING_FLAG]) return

      window[AUTO_ARCHIVE_RUNNING_FLAG] = true
      try {
        // Las reglas (umbral 30d + protección de leads avanzados) viven en
        // autoArchiveStaleLeads — los args quedan por compat pero se ignoran.
        const { archivedIds, error } = await autoArchiveStaleLeads()
        if (error) {
          console.warn("[KanbanColumn] autoArchiveStaleLeads error:", error)
        } else if (Array.isArray(archivedIds) && archivedIds.length) {
          // marcar en memoria para evitar re-procesar en esta sesión
          archivedIds.forEach((id) => processedArchiveRef.current.add(id))
          console.info("[KanbanColumn] auto-archived leads:", archivedIds)
        }
      } catch (err) {
        console.warn("[KanbanColumn] auto-archive failed:", err)
      } finally {
        if (mounted) {
          markRun()
        }
        window[AUTO_ARCHIVE_RUNNING_FLAG] = false
      }
    }

    runAutoArchive()

    return () => {
      mounted = false
      window[AUTO_ARCHIVE_RUNNING_FLAG] = false
    }
  }, [])

  // --- DETECCIÓN MOBILE: reemplaza layout por vista móvil cuando ancho <= 768px ---
  const [isMobileView, setIsMobileView] = useState(false)
  useEffect(() => {
    if (typeof window === "undefined") return
    const check = () => setIsMobileView(window.innerWidth <= 768)
    check()
    window.addEventListener("resize", check)
    return () => window.removeEventListener("resize", check)
  }, [])

  // Si estamos en móvil, delegamos el render al componente móvil (sin drag & drop)
  if (isMobileView) {
    return (
      <KanbanColumnMobile
        column={column}
        leads={leads}
        onViewDetails={onViewDetails}
        onOpenConversation={onOpenConversation}
        onArchive={(leadId) => {
          // archivado rápido desde móvil (cliente)
          if (!leadId) return
          updateLead(leadId, {
            estado_lead: "archivado",
            archivado: true,
            archived_at: new Date().toISOString(),
            archived_reason: "Archivado desde vista móvil",
          }).catch((e) => console.warn("[mobile] archive failed", e))
        }}
      />
    )
  }

  // Usar flex-none + min/max width para que las columnas no se encojan ni se solapen
  // height fijo para mantener todas las columnas iguales; el contenido interno hace scroll.
  const computedBorder = dragOver
    ? "2px dashed rgba(56,189,248,0.6)"
    : hasTrackedLead
      ? (isDark ? "2px dotted rgba(212,0,40,0.5)" : "2px dotted rgba(212,0,40,0.35)")
      : (isDark ? "2px solid rgba(212,0,40,0.15)" : "2px solid rgba(212,0,40,0.08)")

  return (
    <div
      // contenedor principal de columna (más compacto y responsivo)
      className="relative mx-0.5 my-2 flex h-full w-full flex-col overflow-hidden rounded-2xl shadow-xl shadow-rose-900/5 transition-all dark:shadow-rose-900/10"
      style={{
        height: minColumnHeightStyle,
        minHeight: minColumnHeightStyle,
        maxHeight: minColumnHeightStyle,
        width: "100%",
        minWidth: "240px",
        maxWidth: columnWidthClamp,
        background: dragOver ? dragOverBackground : baseBackground,
        border: computedBorder,
        zIndex: 0,
      }}
      onDragOver={(e) => { handleDragOver(e); setDragOver(true) }}
      onDragLeave={(e) => { handleDragLeave(e); setDragOver(false) }}
      onDrop={(e) => { handleDrop(e); setDragOver(false) }}
    >
      {/* Header limpio y compacto — tamaño intermedio */}
      <div
        className="rounded-t-2xl px-2.5 py-2 z-10"
        style={{ minHeight: 48, background: headerBackground, backgroundBlendMode: dragOver ? "multiply" : "normal" }}
      >
        {/* Fila única: Icono + Título + Contador + Botones */}
        <div className="flex items-center gap-2">
          {/* Icono de columna */}
          <div
            className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border shadow-sm"
            style={{
              borderColor: isDark ? `${accent}a0` : `${accent}70`,
              background: isDark
                ? `linear-gradient(145deg, color-mix(in oklab, ${accent} 35%, #000), color-mix(in oklab, ${accent} 60%, #000))`
                : `linear-gradient(145deg, ${accent}25 0%, ${accent}12 100%)`,
            }}
          >
            <ColumnIcon
              className="relative h-4 w-4 drop-shadow-sm"
              aria-hidden="true"
              style={{ color: isDark ? "#f8fafc" : accent }}
            />
          </div>

          {/* Título */}
          <h2
            className="flex-1 min-w-0 text-[12.5px] font-bold leading-tight truncate"
            style={{ color: isDark ? "#f8fafc" : accent }}
          >
            {column.title}
          </h2>

          {/* Contador en pill */}
          <span
            className="flex-shrink-0 inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-md text-[11px] font-bold leading-none border"
            style={{
              borderColor: isDark ? `${accent}80` : `${accent}50`,
              background: isDark
                ? `color-mix(in oklab, ${accent} 25%, rgba(0,0,0,0.5))`
                : `${accent}10`,
              color: isDark ? "#f8fafc" : accent,
            }}
          >
            {statsCount}
          </span>

          {/* Botón de filtros */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex-shrink-0 inline-flex items-center justify-center h-6 w-6 rounded-md border transition ${
              showFilters
                ? "bg-white/90 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-white dark:border-slate-600"
                : "bg-white/40 text-slate-600 hover:bg-white/70 border-slate-300/40 dark:bg-white/5 dark:text-white/70 dark:hover:bg-white/15 dark:border-white/15"
            }`}
            title="Filtros y opciones"
          >
            <Filter className="h-3.5 w-3.5" />
          </button>

        </div>

        {/* Espacio para filtros expandibles */}
        <div className="space-y-2 mt-2">

          {/* filtros expandibles (ocupan línea propia, sin solapamiento con header) */}
          {showFilters && (
            <div className="mt-1 space-y-1.5 bg-white/95 dark:bg-slate-800/95 p-2 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm z-20">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar..."
                  className="h-7 pl-8 pr-7 text-[11px] rounded-md"
                />
                {searchTerm && (
                  <button
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700"
                    onClick={() => setSearchTerm('')}
                  >
                    <X className="h-3 w-3 text-slate-500" />
                  </button>
                )}
              </div>

              <div className="flex flex-wrap gap-1 text-[10px]">
                {[
                  { key: "recent", label: "🔥", title: "Calidad" },
                  { key: "newest", label: "Nuevos", icon: SortDescending },
                  { key: "oldest", label: "Antiguos", icon: ArrowDownAZ },
                  { key: "last_activity", label: "Actividad", icon: Clock },
                  { key: "name", label: "A-Z", icon: ArrowDownAZ },
                ].map(({ key, label, icon: SortIcon, title }) => (
                  <button
                    key={key}
                    onClick={() => setSortOrder(key)}
                    title={title || label}
                    className={`px-1.5 py-1 rounded-md flex items-center gap-0.5 ${sortOrder === key
                      ? 'bg-rose-100 border border-rose-300 text-rose-700 font-semibold dark:bg-rose-900/30 dark:border-rose-700 dark:text-rose-300'
                      : 'bg-slate-100 dark:bg-slate-700/70 hover:bg-slate-200 border border-slate-200 dark:border-slate-600'
                      }`}
                  >
                    {SortIcon ? <SortIcon className="h-2.5 w-2.5" /> : null}
                    {label}
                  </button>
                ))}
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="ml-auto bg-slate-100 dark:bg-slate-700/70 text-[10px] px-1.5 py-1 rounded-md border border-slate-200 dark:border-slate-600"
                >
                  {itemsPerPageOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.value}</option>
                  ))}
                </select>
              </div>

              {/* Indicadores compactos en una fila */}
              {(hiddenCountInColumn > 0 || searchTerm || sortOrder !== 'recent') && (
                <div className="flex items-center gap-1.5">
                  {hiddenCountInColumn > 0 && (
                    <button
                      onClick={restoreHiddenLeads}
                      className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-[9px] font-medium hover:bg-amber-100 transition-colors"
                    >
                      <EyeOff className="h-2.5 w-2.5" />
                      {hiddenCountInColumn} oculto{hiddenCountInColumn !== 1 ? 's' : ''}
                    </button>
                  )}
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-200/80 text-slate-600 hover:bg-slate-300 border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 text-[9px] font-medium transition-colors ml-auto"
                  >
                    <RotateCcw className="h-2.5 w-2.5" />
                    Limpiar
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Lista de leads: ocupa el resto y permite scroll; el contenedor tiene height fijo arriba */}
      <div
        className="flex-1 overflow-y-auto p-2 sm:p-2.5 space-y-1.5"
        ref={containerRef}
        style={{ WebkitOverflowScrolling: "touch", scrollBehavior: "smooth" }}
      >
        {paginatedLeads.length === 0 ? (
          <div className="flex h-full items-center justify-center py-12">
            <div className="text-center px-4">
              <div
                className="mx-auto h-16 w-16 rounded-2xl flex items-center justify-center mb-4 shadow-lg"
                style={{
                  background: isDark
                    ? `linear-gradient(135deg, color-mix(in srgb, ${accent} 20%, #1a070c), color-mix(in srgb, ${accent} 10%, #0f0507))`
                    : `linear-gradient(135deg, ${accent}15, ${accent}08)`,
                  border: `1px solid ${isDark ? accent + '30' : accent + '20'}`,
                }}
              >
                <ColumnIcon className="h-7 w-7" style={{ color: accent, opacity: 0.8 }} />
              </div>
              <p className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">
                {column.emptyMessage || "Sin prospectos aqui"}
              </p>
              <p className="text-[11px] text-slate-400 dark:text-slate-500 max-w-[200px] mx-auto leading-relaxed">
                Arrastra tarjetas a esta columna o espera nuevos leads del bot
              </p>
              <div
                className="mt-4 mx-auto h-1 w-12 rounded-full opacity-40"
                style={{ backgroundColor: accent }}
              />
            </div>
          </div>
        ) : (
          <AnimatePresence mode="popLayout" initial={false}>
            {paginatedLeads.map((lead) => (
              <motion.div
                key={lead.id}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.2 } }}
                ref={highlightedLeadId === lead.id ? highlightedRef : null}
                className={`rounded-xl transition-all ${getUrgencyClass(lead)} ${highlightedLeadId === lead.id ? "ring-2 ring-amber-300 scale-105 z-10" : ""}`}
                style={{ minHeight: leadMinHeight, boxShadow: "0 1px 6px #e5e7eb40" }}
              >
                <LeadCard
                  lead={lead}
                  onViewDetails={onViewDetails}
                  onOpenConversation={onOpenConversation}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  isDragging={isDragging}
                  accent={accent}
                  isHighlighted={highlightedLeadId === lead.id}
                  isCompact={isCompactView}
                  onHide={handleHideLead}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* PAGINACIÓN / FOOTER / ARCHIVE ZONE */}
      {totalPages > 1 && (
        <div className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-2 rounded-b-3xl">
          <div className="flex items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-600 dark:text-slate-300">
                {page}/{totalPages}
              </span>
              <span className="text-slate-400">
                {startIndex + 1}-{endIndex} de {filteredLeads.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { setPage(p => Math.max(1, p - 1)); if (containerRef.current) containerRef.current.scrollTop = 0 }}
                disabled={page === 1}
                className={`h-7 w-7 rounded bg-slate-100 dark:bg-slate-800 text-slate-400 flex items-center justify-center transition-all ${page === 1 ? "opacity-50" : "hover:bg-slate-200 dark:hover:bg-slate-700"}`}
                title="Anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {totalPages <= 5 && Array.from({ length: totalPages }, (_, i) => i + 1).map(pg => (
                <button
                  key={pg}
                  onClick={() => { setPage(pg); if (containerRef.current) containerRef.current.scrollTop = 0 }}
                  className={`h-7 w-7 rounded font-semibold text-xs flex items-center justify-center transition-all ${pg === page
                    ? "bg-rose-100 dark:bg-rose-900 text-rose-700 dark:text-rose-300"
                    : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                    }`}
                >
                  {pg}
                </button>
              ))}
              {totalPages > 5 && (
                <span className="text-slate-400 px-1">...</span>
              )}
              <button
                onClick={() => { setPage(p => Math.min(totalPages, p + 1)); if (containerRef.current) containerRef.current.scrollTop = 0 }}
                disabled={page === totalPages}
                className={`h-7 w-7 rounded bg-slate-100 dark:bg-slate-800 text-slate-400 flex items-center justify-center transition-all ${page === totalPages ? "opacity-50" : "hover:bg-slate-200 dark:hover:bg-slate-700"}`}
                title="Siguiente"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}


      {/* {totalPages === 1 && leads.length > 0 && (
        <div className="border-t border-slate-200 dark:border-slate-800 p-2 bg-white dark:bg-slate-900 rounded-b-3xl text-xs text-center text-slate-400">
          {leads.length} lead{leads.length !== 1 ? "s" : ""}
        </div>
      )} */}

      {/* Zona de archivado: footer ancho y accesible (drop target) */}
      <footer
        role="button"
        aria-label="Zona de archivado"
        className={`mt-3 flex items-center justify-center gap-2 rounded-b-3xl border-t-2 px-4 py-3 text-sm font-medium transition-colors ${archiveZoneHover
          ? "bg-gradient-to-r from-orange-50 to-red-50 border-orange-300 dark:from-orange-900/20 dark:to-red-900/20 dark:border-orange-700 shadow-md"
          : "bg-slate-50 dark:bg-slate-900/70 border-t-slate-200 dark:border-t-slate-700"
          }`}
        onDragOver={(e) => {
          e.preventDefault()
          e.stopPropagation()
          e.dataTransfer.dropEffect = "move"
          setArchiveZoneHover(true)
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setArchiveZoneHover(false)
        }}
        onDrop={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setArchiveZoneHover(false)
          // Llamada al handler pasado desde el padre
          try {
            onArchive()
          } catch (err) {
            console.warn("[KanbanColumn] onArchive error:", err)
          }
        }}
      >
        <div className="flex items-center gap-3">
          <Archive className={`h-4 w-4 ${archiveZoneHover ? "text-orange-600 dark:text-orange-300" : "text-slate-500 dark:text-slate-400"}`} />
          <div className="text-xs leading-tight">
            <div className={`${archiveZoneHover ? "text-orange-800 dark:text-orange-200 font-semibold" : "text-slate-700 dark:text-slate-300"}`}>
              {archiveZoneHover ? "Suelta para archivar" : "Arrastra aquí para archivar"}
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-500">
              Los leads se moverán a estado "archivado"
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
